/* ConfigureFields — /admin/fields
   Columns match old FE: Name, Key, Type, Category, Source Table, Case Types, Actions
   Sub-tabs: Fields | Categories
   Edit modal: form for field properties (defensive Array.isArray on rules/categories) */
import { useState, useMemo, useEffect } from 'react';
import css from '../AdminPage.module.css';
import Badge from '../../../components/ui/Badge/Badge';
import EmptyState from '../../../components/ui/EmptyState/EmptyState';
import SearchInput from '../../../components/ui/SearchInput/SearchInput';
import Pagination from '../../../components/ui/Pagination/Pagination';
import {
  useFieldDefinitions,
  useFieldCategories,
  useCreateField,
  useUpdateField,
  useDeleteField,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useAdminCaseTypes,
  useUpdateFieldCaseTypes,
  useGovernanceCapacity,
  useFieldOptions,
  useCreateFieldOption,
  useUpdateFieldOption,
  useDeleteFieldOption,
  type FieldOption,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type { FieldDefinition, FieldCategory, FieldConfigError } from '../../../types/admin';

/* Phase 4: db_type list aligned with fieldConfigValidator.ALLOWED_DB_TYPES. */
const DB_TYPES = [
  'TEXT',
  'VARCHAR',
  'INTEGER',
  'BIGINT',
  'NUMERIC',
  'BOOLEAN',
  'DATE',
  'TIMESTAMP',
  'TIMESTAMPTZ',
  'JSONB',
];

function extractFieldError(err: unknown): { message: string; details?: string } {
  const e = err as { response?: { data?: FieldConfigError } };
  const body = e?.response?.data;
  if (!body) return { message: 'Failed to save field' };
  const detailLines = (body.details || []).map((d) => `• ${d.path}: ${d.message}`).join('\n');
  return {
    message: body.message || body.code || 'Failed to save field',
    details: detailLines || undefined,
  };
}

type Tab = 'fields' | 'categories';

export default function ConfigureFields() {
  const { data: fields = [], isLoading: loadingFields } = useFieldDefinitions();
  const { data: rawCategories } = useFieldCategories();
  const { data: rawCaseTypes } = useAdminCaseTypes();
  const { data: capacity } = useGovernanceCapacity();
  const { showToast } = useToast();

  /* Defensive: ensure always arrays */
  const categories = Array.isArray(rawCategories) ? rawCategories : [];
  const caseTypes = Array.isArray(rawCaseTypes) ? rawCaseTypes : [];

  const [tab, setTab] = useState<Tab>('fields');
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [fieldPage, setFieldPage] = useState(1);
  const FIELDS_PER_PAGE = 15;

  /* ── Fields CRUD ── */
  const createField = useCreateField();
  const updateField = useUpdateField();
  const deleteField = useDeleteField();
  const updateFieldCaseTypes = useUpdateFieldCaseTypes();

  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState<FieldDefinition | null>(null);
  const [fName, setFName] = useState('');
  const [fKey, setFKey] = useState('');
  const [fType, setFType] = useState('text');
  const [fCategory, setFCategory] = useState('');
  const [fDbType, setFDbType] = useState('TEXT');
  const [fRequired, setFRequired] = useState(false);
  const [fUnique, setFUnique] = useState(false);
  const [fDefault, setFDefault] = useState('');
  const [fPlaceholder, setFPlaceholder] = useState('');
  const [fHelp, setFHelp] = useState('');
  const [fOrder, setFOrder] = useState<number | ''>('');
  const [fValidation, setFValidation] = useState('');
  const [fCaseTypes, setFCaseTypes] = useState<number[]>([]);

  /* ── Field Options (for dropdown/multiple types) ── */
  const [fOptions, setFOptions] = useState<FieldOption[]>([]);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [newOptionLabel, setNewOptionLabel] = useState('');

  const { data: loadedOptions = [] } = useFieldOptions(editingField?.id ?? null);
  const createFieldOption = useCreateFieldOption();
  const updateFieldOption = useUpdateFieldOption();
  const deleteFieldOption = useDeleteFieldOption();

  /* ── Categories CRUD ── */
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const [showCatModal, setShowCatModal] = useState(false);
  const [editingCat, setEditingCat] = useState<FieldCategory | null>(null);
  const [catName, setCatName] = useState('');
  const [catOrder, setCatOrder] = useState(0);

  const filteredFields = useMemo(() => {
    let list = Array.isArray(fields) ? fields : [];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (f) =>
          (f.name || '').toLowerCase().includes(q) ||
          (f.key || '').toLowerCase().includes(q) ||
          (f.label || '').toLowerCase().includes(q),
      );
    }
    if (catFilter) list = list.filter((f) => (f.category_name || f.category) === catFilter);
    return list;
  }, [fields, search, catFilter]);

  /* Sync loaded options when editing a field */
  useEffect(() => {
    if (editingField && loadedOptions.length > 0) {
      setFOptions(
        loadedOptions.map((opt, idx) => ({
          id: opt.id,
          value: opt.value || '',
          label: opt.label || opt.value || '',
          display_order: opt.display_order ?? idx + 1,
        })),
      );
    }
  }, [editingField, loadedOptions]);

  /* ── Field helpers ── */
  function openNewField() {
    setEditingField(null);
    setFName('');
    setFKey('');
    setFType('text');
    setFCategory('');
    setFDbType('TEXT');
    setFRequired(false);
    setFUnique(false);
    setFDefault('');
    setFPlaceholder('');
    setFHelp('');
    setFOrder('');
    setFValidation('');
    setFCaseTypes([]);
    setFOptions([]);
    setNewOptionValue('');
    setNewOptionLabel('');
    setShowFieldModal(true);
  }
  function openEditField(f: FieldDefinition) {
    setEditingField(f);
    setFName(f.name || f.label || '');
    setFKey(f.key || '');
    setFType(f.field_type || f.type || 'text');
    setFCategory(f.category_name || f.category || '');
    setFDbType((f.db_type || 'TEXT').toUpperCase());
    setFRequired(!!f.is_required);
    setFUnique(!!f.is_unique);
    setFDefault(f.default_value || '');
    setFPlaceholder(f.placeholder_text || f.placeholder || '');
    setFHelp(f.help_text || '');
    setFOrder(typeof f.display_order === 'number' ? f.display_order : '');
    setFValidation(
      f.validation_rules == null
        ? ''
        : typeof f.validation_rules === 'string'
          ? f.validation_rules
          : JSON.stringify(f.validation_rules, null, 2),
    );
    setFCaseTypes(
      Array.isArray(f.case_types)
        ? f.case_types
        : Array.isArray(f.case_type_ids)
          ? f.case_type_ids
          : [],
    );
    setFOptions(
      Array.isArray(f.options)
        ? f.options.map((opt: unknown, idx: number) => {
            const option = opt as Partial<FieldOption>;
            return {
              id: option.id,
              value: option.value || '',
              label: option.label || option.value || '',
              display_order: idx + 1,
            };
          })
        : [],
    );
    setNewOptionValue('');
    setNewOptionLabel('');
    setShowFieldModal(true);
  }
  async function saveField() {
    /* Phase 4: canonical payload keys — server validator rejects legacy aliases
       at the boundary, but we already use the canonical names client-side. */
    let parsedValidation: unknown = undefined;
    if (fValidation.trim()) {
      try {
        parsedValidation = JSON.parse(fValidation);
      } catch {
        showToast('Validation rules must be valid JSON', 'error');
        return;
      }
    }
    const payload: Record<string, unknown> = {
      field_name: fName,
      field_type: fType,
      category_id: categories.find((c) => c.name === fCategory)?.id ?? null,
      db_type: fDbType,
      is_required: fRequired,
      is_unique: fUnique,
      default_value: fDefault || null,
      placeholder_text: fPlaceholder || null,
      help_text: fHelp || null,
      display_order: fOrder === '' ? null : Number(fOrder),
      validation_rules: parsedValidation ?? null,
      case_types: fCaseTypes,
    };
    if (!editingField || fKey !== editingField.key) payload.field_key = fKey;

    // Include options for dropdown/multiple fields (only for new fields)
    if (!editingField && (fType === 'dropdown' || fType === 'multiple') && fOptions.length > 0) {
      payload.options = fOptions.map((opt) => ({
        value: opt.value,
        label: opt.label || opt.value,
      }));
    }

    try {
      if (editingField) {
        await updateField.mutateAsync({
          id: editingField.id,
          ...(payload as Partial<FieldDefinition>),
        });
        await updateFieldCaseTypes.mutateAsync({ id: editingField.id, caseTypeIds: fCaseTypes });
      } else {
        const res = await createField.mutateAsync(payload as Partial<FieldDefinition>);
        const newId = (res?.data as { id?: number } | undefined)?.id;
        if (newId && fCaseTypes.length > 0) {
          await updateFieldCaseTypes.mutateAsync({ id: newId, caseTypeIds: fCaseTypes });
        }
      }
      showToast(editingField ? 'Field updated' : 'Field created', 'success');
      setShowFieldModal(false);
    } catch (err) {
      const { message, details } = extractFieldError(err);
      showToast(details ? `${message}\n${details}` : message, 'error');
    }
  }
  async function removeField(id: number) {
    if (!confirm('Delete this field definition?')) return;
    try {
      await deleteField.mutateAsync(id);
      showToast('Field deleted', 'success');
    } catch {
      showToast('Failed to delete field', 'error');
    }
  }

  /* ── Category helpers ── */
  function openNewCat() {
    setEditingCat(null);
    setCatName('');
    setCatOrder(categories.length);
    setShowCatModal(true);
  }
  function openEditCat(c: FieldCategory) {
    setEditingCat(c);
    setCatName(c.name);
    setCatOrder(c.sort_order || 0);
    setShowCatModal(true);
  }
  async function saveCat() {
    try {
      if (editingCat) {
        await updateCategory.mutateAsync({
          id: editingCat.id,
          name: catName,
          sort_order: catOrder,
        });
      } else {
        await createCategory.mutateAsync({ name: catName, sort_order: catOrder });
      }
      showToast(editingCat ? 'Category updated' : 'Category created', 'success');
      setShowCatModal(false);
    } catch {
      showToast('Failed to save category', 'error');
    }
  }
  async function removeCat(id: number) {
    if (!confirm('Delete this category?')) return;
    try {
      await deleteCategory.mutateAsync(id);
      showToast('Category deleted', 'success');
    } catch {
      showToast('Failed to delete category', 'error');
    }
  }

  function toggleCaseType(id: number) {
    setFCaseTypes((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  /* ── Field Option helpers ── */
  async function addOption() {
    if (!newOptionValue.trim()) {
      showToast('Option value is required', 'error');
      return;
    }

    // For new fields, just add to local state
    if (!editingField) {
      const newOpt: FieldOption = {
        value: newOptionValue.trim(),
        label: newOptionLabel.trim() || newOptionValue.trim(),
        display_order: fOptions.length + 1,
      };
      setFOptions([...fOptions, newOpt]);
      setNewOptionValue('');
      setNewOptionLabel('');
      return;
    }

    // For existing fields, save to backend
    try {
      await createFieldOption.mutateAsync({
        fieldId: editingField.id,
        value: newOptionValue.trim(),
        label: newOptionLabel.trim() || newOptionValue.trim(),
        display_order: fOptions.length + 1,
      });
      setNewOptionValue('');
      setNewOptionLabel('');
      showToast('Option added', 'success');
    } catch {
      showToast('Failed to add option', 'error');
    }
  }

  async function removeOption(opt: FieldOption, index: number) {
    if (!editingField || !opt.id) {
      // For new fields or unsaved options, just remove from local state
      setFOptions(fOptions.filter((_, i) => i !== index));
      return;
    }

    // For existing saved options, delete from backend
    if (!confirm('Delete this option?')) return;
    try {
      await deleteFieldOption.mutateAsync({ fieldId: editingField.id, id: opt.id });
      showToast('Option deleted', 'success');
    } catch {
      showToast('Failed to delete option', 'error');
    }
  }

  async function moveOption(index: number, direction: 'up' | 'down') {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === fOptions.length - 1)
    ) {
      return;
    }

    const newOptions = [...fOptions];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newOptions[index], newOptions[swapIndex]] = [newOptions[swapIndex], newOptions[index]];

    // Update display_order
    newOptions.forEach((opt, i) => {
      opt.display_order = i + 1;
    });

    setFOptions(newOptions);

    // If editing existing field, update backend
    if (editingField) {
      try {
        await Promise.all(
          newOptions
            .slice(Math.min(index, swapIndex), Math.max(index, swapIndex) + 1)
            .filter((opt) => opt.id)
            .map((opt) =>
              updateFieldOption.mutateAsync({
                fieldId: editingField.id,
                id: opt.id!,
                value: opt.value,
                label: opt.label,
                display_order: opt.display_order,
              }),
            ),
        );
      } catch {
        showToast('Failed to update option order', 'error');
      }
    }
  }

  if (loadingFields) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <h2 className={css.sectionTitle}>📋 Configure Data Fields</h2>
        </div>

        {/* Tabs */}
        <div className={css.tabs}>
          <button
            className={tab === 'fields' ? css.tabActive : css.tab}
            onClick={() => setTab('fields')}
          >
            Fields ({fields.length})
          </button>
          <button
            className={tab === 'categories' ? css.tabActive : css.tab}
            onClick={() => setTab('categories')}
          >
            Categories ({categories.length})
          </button>
        </div>

        {/* ─── Fields tab ─── */}
        {tab === 'fields' && (
          <>
            {/* Phase 4 capacity widget */}
            {capacity &&
              (() => {
                const f = capacity.fields;
                const pct =
                  f.limit > 0 ? Math.min(100, Math.round((f.current / f.limit) * 100)) : 0;
                const tone = !f.ok
                  ? 'var(--red-500)'
                  : f.warning
                    ? 'var(--orange-500)'
                    : 'var(--primary-500)';
                return (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 14px',
                      borderRadius: 8,
                      background: 'var(--gray-50)',
                      border: '1px solid var(--gray-200)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                      Field capacity: {f.current.toLocaleString()} / {f.limit.toLocaleString()}
                    </div>
                    <div
                      style={{
                        flex: 1,
                        height: 8,
                        background: 'var(--gray-200)',
                        borderRadius: 4,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${pct}%`,
                          height: '100%',
                          background: tone,
                          transition: 'width 0.3s',
                        }}
                      />
                    </div>
                    {f.warning && (
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--orange-600)' }}>
                        ⚠ {f.warning.message}
                      </div>
                    )}
                  </div>
                );
              })()}
            <div className={css.toolbar} style={{ marginBottom: 16 }}>
              <SearchInput
                placeholder="Search fields..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className={css.formSelect}
                value={catFilter}
                onChange={(e) => setCatFilter(e.target.value)}
                style={{ maxWidth: 180 }}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button className={css.btnPrimary} onClick={openNewField}>
                + New Field
              </button>
            </div>
            {filteredFields.length === 0 ? (
              <EmptyState message="No fields found" />
            ) : (
              <>
                <div style={{ overflowX: 'auto', width: '100%' }}>
                  <table className={css.table} style={{ minWidth: 900 }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Key</th>
                        <th>Type</th>
                        <th>Storage</th>
                        <th>Category</th>
                        <th>Case Types</th>
                        <th style={{ width: 50 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFields
                        .slice((fieldPage - 1) * FIELDS_PER_PAGE, fieldPage * FIELDS_PER_PAGE)
                        .map((f) => (
                          <tr
                            key={f.id}
                            onClick={() => openEditField(f)}
                            style={{ cursor: 'pointer' }}
                            title="Click to edit"
                          >
                            <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {f.name || f.label}
                            </td>
                            <td
                              style={{
                                fontFamily: 'monospace',
                                fontSize: 'var(--font-size-xs)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {f.key}
                            </td>
                            <td>
                              <Badge variant="neutral">{f.field_type || f.type}</Badge>
                            </td>
                            <td>
                              <Badge variant={f.storage_mode === 'eav' ? 'success' : 'neutral'}>
                                {f.storage_mode === 'eav' ? 'EAV' : 'Column'}
                              </Badge>
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              {f.category_name || f.category || '\u2014'}
                            </td>
                            <td
                              style={{
                                fontSize: 'var(--font-size-xs)',
                                maxWidth: 220,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {Array.isArray(f.case_type_names) && f.case_type_names.length > 0 ? (
                                f.case_type_names.join(', ')
                              ) : (
                                <span style={{ color: 'var(--gray-400)' }}>All</span>
                              )}
                            </td>
                            <td>
                              <button
                                className={`${css.btnDanger} ${css.btnSmall}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeField(f.id);
                                }}
                              >
                                Del
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {filteredFields.length > FIELDS_PER_PAGE && (
                  <Pagination
                    page={fieldPage}
                    totalPages={Math.ceil(filteredFields.length / FIELDS_PER_PAGE)}
                    total={filteredFields.length}
                    label="fields"
                    onPageChange={setFieldPage}
                  />
                )}
              </>
            )}
          </>
        )}

        {/* ─── Categories tab ─── */}
        {tab === 'categories' && (
          <>
            <div className={css.toolbar} style={{ marginBottom: 16 }}>
              <button className={css.btnPrimary} onClick={openNewCat}>
                + New Category
              </button>
            </div>
            {categories.length === 0 ? (
              <EmptyState message="No categories" />
            ) : (
              <table className={css.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Sort Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...categories]
                    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                    .map((c) => (
                      <tr key={c.id}>
                        <td style={{ fontWeight: 600 }}>{c.name}</td>
                        <td>{c.sort_order ?? 0}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              className={`${css.btnSecondary} ${css.btnSmall}`}
                              onClick={() => openEditCat(c)}
                            >
                              Edit
                            </button>
                            <button
                              className={`${css.btnDanger} ${css.btnSmall}`}
                              onClick={() => removeCat(c.id)}
                            >
                              Del
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>

      {/* ── Field modal ── */}
      {showFieldModal && (
        <div className={css.modalBackdrop} onClick={() => setShowFieldModal(false)}>
          <div
            className={css.modalLg || css.modal}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 600 }}
          >
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>{editingField ? 'Edit Field' : 'New Field'}</h3>
              <button className={css.modalClose} onClick={() => setShowFieldModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Name</label>
                  <input
                    className={css.formInput}
                    value={fName}
                    onChange={(e) => setFName(e.target.value)}
                    placeholder="Field Name"
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Key</label>
                  <input
                    className={css.formInput}
                    value={fKey}
                    onChange={(e) => setFKey(e.target.value)}
                    placeholder="field_key"
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Type</label>
                  <select
                    className={css.formSelect}
                    value={fType}
                    onChange={(e) => setFType(e.target.value)}
                  >
                    <option value="text">Text</option>
                    <option value="string">String</option>
                    <option value="textarea">Textarea</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="multiple">Multiple Select</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="currency">Currency</option>
                  </select>
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Category</label>
                  <select
                    className={css.formSelect}
                    value={fCategory}
                    onChange={(e) => setFCategory(e.target.value)}
                  >
                    <option value="">None</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>DB Type</label>
                  <select
                    className={css.formSelect}
                    value={fDbType}
                    onChange={(e) => setFDbType(e.target.value)}
                  >
                    {DB_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* Storage badge (read-only) — Phase 4 EAV migration */}
              {editingField && (
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Storage</label>
                  <Badge variant={editingField.storage_mode === 'eav' ? 'success' : 'neutral'}>
                    {editingField.storage_mode === 'eav'
                      ? 'EAV (olap.case_fields)'
                      : 'Column (legacy dim table)'}
                  </Badge>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-500)',
                      marginTop: 4,
                    }}
                  >
                    New fields are managed via EAV automatically. Legacy column-mode fields cannot
                    change storage here.
                  </p>
                </div>
              )}
              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Default Value</label>
                  <input
                    className={css.formInput}
                    value={fDefault}
                    onChange={(e) => setFDefault(e.target.value)}
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Placeholder</label>
                  <input
                    className={css.formInput}
                    value={fPlaceholder}
                    onChange={(e) => setFPlaceholder(e.target.value)}
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Display Order</label>
                  <input
                    className={css.formInput}
                    type="number"
                    value={fOrder}
                    onChange={(e) => setFOrder(e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </div>
                <div className={css.formGroup}>
                  <label
                    className={css.formLabel}
                    style={{ display: 'flex', gap: 16, alignItems: 'center', paddingTop: 24 }}
                  >
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={fRequired}
                        onChange={(e) => setFRequired(e.target.checked)}
                      />
                      Required
                    </span>
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={fUnique}
                        onChange={(e) => setFUnique(e.target.checked)}
                      />
                      Unique
                    </span>
                  </label>
                </div>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Help Text</label>
                <textarea
                  className={css.formInput}
                  rows={2}
                  value={fHelp}
                  onChange={(e) => setFHelp(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Validation Rules (JSON, optional)</label>
                <textarea
                  className={css.formInput}
                  rows={3}
                  value={fValidation}
                  onChange={(e) => setFValidation(e.target.value)}
                  placeholder='e.g. {"min":0,"max":100}'
                  style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Case Type Restrictions</label>
                <p
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--gray-500)',
                    marginBottom: 6,
                  }}
                >
                  Leave all unchecked to apply to all case types
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {caseTypes.map((ct) => {
                    const isSelected = fCaseTypes.includes(ct.id);
                    return (
                      <button
                        key={ct.id}
                        type="button"
                        onClick={() => toggleCaseType(ct.id)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '5px 12px',
                          borderRadius: 9999,
                          border: `1.5px solid ${isSelected ? 'var(--primary-400)' : 'var(--gray-300)'}`,
                          background: isSelected ? 'var(--primary-50)' : 'var(--gray-50)',
                          color: isSelected ? 'var(--primary-700)' : 'var(--gray-500)',
                          fontWeight: isSelected ? 600 : 400,
                          fontSize: 'var(--font-size-sm)',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: `1.5px solid ${isSelected ? 'var(--primary-500)' : 'var(--gray-300)'}`,
                            background: isSelected ? 'var(--primary-500)' : 'transparent',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: 10,
                            lineHeight: 1,
                          }}
                        >
                          {isSelected ? '✓' : ''}
                        </span>
                        {ct.display_name || ct.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* ── Dropdown Options (for dropdown/multiple types) ── */}
              {(fType === 'dropdown' || fType === 'multiple') && (
                <div className={css.formGroup}>
                  <label className={css.formLabel}>
                    Dropdown Options
                    {fType === 'multiple' && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--gray-500)',
                        }}
                      >
                        (Multiple Select)
                      </span>
                    )}
                  </label>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-500)',
                      marginBottom: 8,
                    }}
                  >
                    Define the available options for this {fType} field
                  </p>

                  {/* Existing Options */}
                  {fOptions.length > 0 && (
                    <div
                      style={{
                        marginBottom: 12,
                        border: '1px solid var(--gray-200)',
                        borderRadius: 6,
                        overflow: 'hidden',
                      }}
                    >
                      {fOptions.map((opt, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 12px',
                            background: idx % 2 === 0 ? 'var(--gray-50)' : 'white',
                            borderBottom:
                              idx < fOptions.length - 1 ? '1px solid var(--gray-200)' : 'none',
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              fontFamily: 'monospace',
                              fontSize: 'var(--font-size-sm)',
                            }}
                          >
                            <strong>{opt.value}</strong>
                            {opt.label && opt.label !== opt.value && (
                              <span style={{ marginLeft: 8, color: 'var(--gray-500)' }}>
                                ({opt.label})
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              type="button"
                              onClick={() => moveOption(idx, 'up')}
                              disabled={idx === 0}
                              className={css.btnSmall}
                              style={{ padding: '2px 6px', opacity: idx === 0 ? 0.3 : 1 }}
                              title="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => moveOption(idx, 'down')}
                              disabled={idx === fOptions.length - 1}
                              className={css.btnSmall}
                              style={{
                                padding: '2px 6px',
                                opacity: idx === fOptions.length - 1 ? 0.3 : 1,
                              }}
                              title="Move down"
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              onClick={() => removeOption(opt, idx)}
                              className={`${css.btnDanger} ${css.btnSmall}`}
                              style={{ padding: '2px 8px' }}
                              title="Delete"
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Option */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--gray-600)',
                          display: 'block',
                          marginBottom: 4,
                        }}
                      >
                        Value (required)
                      </label>
                      <input
                        className={css.formInput}
                        value={newOptionValue}
                        onChange={(e) => setNewOptionValue(e.target.value)}
                        placeholder="option_value"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addOption();
                          }
                        }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--gray-600)',
                          display: 'block',
                          marginBottom: 4,
                        }}
                      >
                        Label (optional)
                      </label>
                      <input
                        className={css.formInput}
                        value={newOptionLabel}
                        onChange={(e) => setNewOptionLabel(e.target.value)}
                        placeholder="Display Label"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addOption();
                          }
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={addOption}
                      className={css.btnSecondary}
                      disabled={!newOptionValue.trim()}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      + Add Option
                    </button>
                  </div>

                  {fOptions.length === 0 && (
                    <p
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--orange-600)',
                        marginTop: 8,
                        padding: '8px 12px',
                        background: 'var(--orange-50)',
                        borderRadius: 4,
                        border: '1px solid var(--orange-200)',
                      }}
                    >
                      ⚠ No options defined. Add at least one option for this {fType} field.
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowFieldModal(false)}>
                Cancel
              </button>
              <button className={css.btnPrimary} onClick={saveField} disabled={!fName || !fKey}>
                {editingField ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Category modal ── */}
      {showCatModal && (
        <div className={css.modalBackdrop} onClick={() => setShowCatModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>{editingCat ? 'Edit Category' : 'New Category'}</h3>
              <button className={css.modalClose} onClick={() => setShowCatModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Name</label>
                <input
                  className={css.formInput}
                  value={catName}
                  onChange={(e) => setCatName(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Sort Order</label>
                <input
                  className={css.formInput}
                  type="number"
                  value={catOrder}
                  onChange={(e) => setCatOrder(Number(e.target.value))}
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowCatModal(false)}>
                Cancel
              </button>
              <button className={css.btnPrimary} onClick={saveCat} disabled={!catName}>
                {editingCat ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
